import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Palette, Truck, Recycle, Star } from "lucide-react";
import api from "@/lib/api";
import Marquee from "@/components/Marquee";
import ProductCard from "@/components/ProductCard";

const HERO_IMG = "https://images.unsplash.com/photo-1689524697054-0b48a722b761?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200";

const perks = [
  { icon: Palette, title: "Design It Yourself", text: "Drop text & art onto any tee in our studio." },
  { icon: Truck, title: "Fast & Free Shipping", text: "On every order over $50. No fine print." },
  { icon: Recycle, title: "Organic Cotton", text: "Soft, heavyweight & planet-friendly fabric." },
];

export default function Home() {
  const [products, setProducts] = useState([]);

  useEffect(() => {
    api.get("/products").then(({ data }) => setProducts(data.slice(0, 4))).catch(() => {});
  }, []);

  return (
    <div data-testid="home-page">
      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 dot-grid" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 pt-14 pb-16 grid gap-10 lg:grid-cols-12 items-center">
          <div className="lg:col-span-6 fade-up">
            <span className="inline-flex items-center gap-2 bg-brand-mint neo-sm rounded-full px-4 py-1.5 text-xs font-bold uppercase tracking-widest mb-6">
              <Star className="w-3.5 h-3.5 fill-ink" /> New Drop Live
            </span>
            <h1 className="font-display font-bold text-5xl sm:text-6xl lg:text-7xl leading-[0.95] tracking-tight">
              Wear it <span className="bg-brand-yellow neo-sm rounded-xl px-3 inline-block -rotate-2">loud.</span><br />
              Make it <span className="bg-brand-pink neo-sm rounded-xl px-3 inline-block rotate-2">yours.</span>
            </h1>
            <p className="mt-6 text-lg font-semibold text-ink/70 max-w-md">
              Bold graphic tees and a build-your-own Design Studio. Fabricate a shirt that's unmistakably you.
            </p>
            <div className="mt-8 flex flex-wrap gap-4">
              <Link to="/shop" className="inline-flex items-center gap-2 bg-ink text-paper font-bold px-6 py-3.5 rounded-xl neo-press border-2 border-ink" data-testid="hero-shop-btn">
                Shop the Drop <ArrowRight className="w-5 h-5" />
              </Link>
              <Link to="/designer" className="inline-flex items-center gap-2 bg-brand-sky font-bold px-6 py-3.5 rounded-xl neo-press border-2 border-ink" data-testid="hero-design-btn">
                <Palette className="w-5 h-5" /> Design a Tee
              </Link>
            </div>
          </div>
          <div className="lg:col-span-6 fade-up" style={{ animationDelay: "0.15s" }}>
            <div className="relative">
              <div className="absolute -top-4 -left-4 w-full h-full bg-brand-mint rounded-3xl border-2 border-ink" />
              <img src={HERO_IMG} alt="Colorful tee" className="relative w-full h-[420px] object-cover rounded-3xl border-2 border-ink" />
              <div className="absolute -bottom-5 -right-3 bg-white neo rounded-2xl px-5 py-3 rotate-3">
                <p className="font-display font-bold text-xl">50k+ tees</p>
                <p className="text-xs font-bold uppercase tracking-wider text-ink/60">fabricated & counting</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Marquee items={["FRESH GRAPHICS", "CUSTOM PRINTS", "ORGANIC COTTON", "FREE SHIPPING $50+", "LIMITED DROPS"]} />

      {/* Perks */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-16 grid gap-6 md:grid-cols-3">
        {perks.map((p) => (
          <div key={p.title} className="bg-white neo rounded-2xl p-6" data-testid={`perk-${p.title.split(" ")[0].toLowerCase()}`}>
            <span className="grid place-items-center w-12 h-12 bg-brand-yellow neo-sm rounded-xl mb-4"><p.icon className="w-6 h-6" /></span>
            <h3 className="font-display font-bold text-xl">{p.title}</h3>
            <p className="mt-1 font-semibold text-ink/70">{p.text}</p>
          </div>
        ))}
      </section>

      {/* Featured products */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 pb-8">
        <div className="flex items-end justify-between mb-8">
          <h2 className="font-display font-bold text-3xl sm:text-4xl">Fresh off the press</h2>
          <Link to="/shop" className="font-bold hover:text-brand-pink flex items-center gap-1" data-testid="view-all-link">
            View all <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {products.map((p) => <ProductCard key={p.product_id} product={p} />)}
        </div>
      </section>

      {/* Design CTA */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-16">
        <div className="bg-brand-pink neo-lg rounded-3xl p-10 sm:p-14 text-center relative overflow-hidden">
          <h2 className="font-display font-bold text-4xl sm:text-5xl">Your idea. Our fabric.</h2>
          <p className="mt-4 font-semibold text-lg max-w-xl mx-auto">
            Pick a color, add your words, drop on some art. Preview it live and make it real.
          </p>
          <Link to="/designer" className="mt-8 inline-flex items-center gap-2 bg-ink text-paper font-bold px-7 py-4 rounded-xl neo-press border-2 border-ink" data-testid="cta-design-btn">
            <Palette className="w-5 h-5" /> Open the Design Studio
          </Link>
        </div>
      </section>
    </div>
  );
}
